import Button from "@/design-system/button";
import TelemetryButton from "@/design-system/telemetry-button";
import { polishCurrencyFormatter } from "@/lib/formatters";
import { languageSelector } from "@/src/contexts";
import ImageSeo from "@Components/Image-seo";
import { TelemetryEntity } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type PropsWithStyleX,
    colors,
    globalLimitStyles,
} from "../../../styles/tokens.stylex";
import Resources from "./PriceComparisonItem.Resources";
import ResourcesEn from "./PriceComparisonItem.Resources.En";
import PriceComparisonItemCashbackLabel from "./PriceComparisonItemCashbackLabel";
import PriceComparisonItemPrice from "./PriceComparisonItemPrice";

export type PriceComparisonItemProps = PropsWithStyleX & {
    id: string;
    name: string | undefined;
    price: string;
    image: string | undefined;
    maxCashback?: number;
    slug: string | undefined;
    mobileView: boolean;
    position: number;
};

export default function PriceComparisonItem({ styleX, mobileView, ...props }: PriceComparisonItemProps) {
    const resources = languageSelector(Resources, ResourcesEn);
    const href = `/produkty/${props.slug},${props.id}`;

    const amount = polishCurrencyFormatter(props.price);
    return (
        <TelemetryButton
            styleX={[styles.itemContainer, mobileView && styles.itemContainerMobile, styleX]}
            href={href}
            isExternal
            as="a"
            size="None"
            eventName={`${TelemetryEntity.Comparison}-product`}
            itemId={`${props.id}`}
            param1={`${props.position}`}
            origin="searchResultsGlobal"
        >
            <div {...stylex.props(styles.imageContainer, mobileView && styles.imageContainerMobile)}>
                <ImageSeo
                    styleX={[styles.image, mobileView && styles.imageMobile]}
                    src={props.image}
                    alt={props.name}
                    size="m"
                />
            </div>
            <div {...stylex.props(styles.textContent, mobileView && styles.textContentMobile)}>
                <p {...stylex.props(globalLimitStyles.TextTwoLinesCap, styles.name)}>{props.name}</p>

                <PriceComparisonItemPrice
                    variant="searchResultsDetails"
                    amount={amount}
                    isMobile={mobileView}
                />

                <PriceComparisonItemCashbackLabel labelText={resources.pcItemCbLabel} />
            </div>
            {!mobileView && (
                <div {...stylex.props(styles.btnGotoDetailsContainer)}>
                    <Button variant="magentaRound" as="button" size="M" styleX={styles.btnGotoDetails}>
                        {resources.pcItemBtn}
                    </Button>
                </div>
            )}
        </TelemetryButton>
    );
}

const styles = stylex.create({
    itemContainer: {
        padding: 0,
        borderColor: colors.bg_Secondary,
        borderRadius: ComposableSpacing.s2,
        borderStyle: "solid",
        borderWidth: "1px",
        color: "inherit",
        display: "block",
        userSelect: "none",
    },
    itemContainerMobile: {
        borderColor: colors.bg_Secondary,
        borderRadius: null,
        borderStyle: "unset",
    },

    imageContainer: {
        aspectRatio: "1/1",

        backgroundColor: colors.bg_Secondary,
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0",
        borderTopLeftRadius: "0.5rem",
        borderTopRightRadius: "0.5rem",
        width: "100%",
    },
    imageContainerMobile: {
        borderRadius: ComposableSpacing.s2,
    },

    image: {
        padding: ComposableSpacing.s8,
        mixBlendMode: "multiply",
        objectFit: "contain",
        height: "100%",
        width: "100%",
    },

    imageMobile: {
        padding: ComposableSpacing.s4,
    },

    name: {
        lineHeight: "1.5em",
        whiteSpace: "initial",
        height: "3em",
    },

    textContent: {
        padding: `${ComposableSpacing.s3} ${ComposableSpacing.s3} 0`,
        gap: ComposableSpacing.s1,
        display: "inline-flex",
        flexDirection: "column",
        borderBottomLeftRadius: "0.5rem",
        borderBottomRightRadius: "0.5rem",
        borderTopColor: colors.bg_Secondary,
        borderTopLeftRadius: "0",
        borderTopRightRadius: "0",
        borderTopStyle: "solid",
        borderTopWidth: "1px",
        width: "100%",
    },
    textContentMobile: {
        padding: `0 ${ComposableSpacing.s1}`,
        borderRadius: ComposableSpacing.s2,
    },

    btnGotoDetails: {
        width: "100%",
    },

    btnGotoDetailsContainer: {
        padding: ComposableSpacing.s3,
        display: "block",
        paddingTop: ComposableSpacing.s4,
    },
});
